import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { greenColor } from '../../../config/colors';
import { useTranslation } from '../../../hooks/useTranslation';

const LinkedResalesModal = ({ open, onClose, simulator, allResales = [] }) => {
  const { t } = useTranslation();
  const [linked, setLinked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!open || !simulator?.id) return;

    setLoading(true);

    api
      .get(`/v1/simulators/${simulator.id}/resales`)
      .then(({ data }) => setLinked(data?.data ?? []))
      .catch(() => toast.error(t('simulatorSuperAdmin.toasts.resalesLinkError')))
      .finally(() => setLoading(false));
  }, [open, simulator, t]);

  const isLinked = (id) => linked.some((r) => r.id === id);

  const handleToggle = async (company) => {
    setSaving(company.id);

    try {
      if (isLinked(company.id)) {
        await api.delete(`/v1/simulators/${simulator.id}/resales/${company.id}`);
        setLinked((prev) => prev.filter((r) => r.id !== company.id));
      } else {
        await api.post(`/v1/simulators/${simulator.id}/resales/${company.id}`);
        setLinked((prev) => [...prev, company]);
      }
    } catch {
      toast.error(t('simulatorSuperAdmin.toasts.linkUpdateError'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t('simulatorSuperAdmin.linkedResalesModal.title')}
      </DialogTitle>

      <DialogContent sx={{ pt: '16px !important' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress sx={{ color: greenColor }} />
          </Box>
        ) : allResales.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            {t('simulatorSuperAdmin.linkedResalesModal.noResales')}
          </Typography>
        ) : (
          allResales.map((r) => (
            <Box
              key={r.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                borderBottom: '1px solid #F3F4F6',
              }}
            >
              <Typography fontSize={14}>
                {r.name?.toUpperCase()}
              </Typography>

              <Button
                size="small"
                variant={isLinked(r.id) ? 'contained' : 'outlined'}
                disabled={saving === r.id}
                onClick={() => handleToggle(r)}
                sx={
                  isLinked(r.id)
                    ? {
                        bgcolor: greenColor,
                        color: '#fff',
                        '&:hover': { bgcolor: '#6ec13b' },
                      }
                    : {}
                }
              >
                {saving === r.id ? (
                  <CircularProgress size={16} />
                ) : isLinked(r.id) ? (
                  t('simulatorSuperAdmin.linkedResalesModal.linked')
                ) : (
                  t('simulatorSuperAdmin.linkedResalesModal.link')
                )}
              </Button>
            </Box>
          ))
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkedResalesModal;
